import { Box, Button, ButtonGroup, makeStyles, Paper } from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { bind } from "@react-rxjs/core";
import { createSignal } from "@react-rxjs/utils";
import blobStream from 'blob-stream';
import StateToPdfMake from 'draft-js-export-pdfmake';
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Content, ContentTable, TableCell, TDocumentDefinitions } from "pdfmake/interfaces";
import React, { useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { concatMap, debounceTime } from "rxjs/operators";

import { DbDoc } from "models/FbDoc";
import { Nullable } from "models/Nullable";
import { Offer } from "models/Offer";
import { OfferRoomPriceField } from "models/OfferRoomPriceField";

import { calculateDiscountPercentage } from './calculatePrices'
import { dateFormat } from "./date.utils";
import { isEmpty } from "./isEmpty";
import { isNumber } from './isNumber'
import { currencyFormat } from './number.utils';
import { sortAlphabetically } from "./sortAlphabetically";

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

const isLocal = window.location.hostname === 'localhost'
const isHostingEmulator = window.location.port === '5000'
const LOGO_URL = `${isLocal ? `http://localhost:${isHostingEmulator ? '5000' : '3000'}` : 'https://trygkalk.web.app'}/icons/logo.svg`

function getBase64ImageFromURL(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    var img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => {
      var canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      var ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      var dataURL = canvas.toDataURL("image/png");
      resolve(dataURL);
    };
    img.onerror = error => {
      reject(error);
    };
    img.src = url;
  });
}

function nullText(text?: Nullable<string>) {
  return isEmpty(text) ? '---' : text
}

function getOfferDetails(offerDoc: DbDoc<Offer>, logoImageFileUrl: string): Content {
  const offer = offerDoc.data
  const comments: Content = offer.comment ? [
    { text: 'Kommentar', style: 'h3' },
    { text: offer.comment, style: 'p' },
  ] : []
  return [
    { image: logoImageFileUrl, width: 50 },
    { text: `Dato: ${dateFormat.format(offerDoc.metadata.createdAt.toDate())}`, style: 'p' },
    { text: 'Oppdragsgiver', style: 'h3' },
    { text: `Navn: ${nullText(offer.contactInfo?.name?.fullName)}`, style: 'p' },
    { text: `E-post: ${nullText(offer.contactInfo?.email?.email)}`, style: 'p' },
    { text: `Telefon: ${nullText(offer.contactInfo?.phone?.phoneNumber)}`, style: 'p' },
    { text: `Adresse: ${nullText(offer.address?.addressString)}`, style: 'p' },
    { text: 'Oppdragstaker', style: 'h3' },
    { text: `Navn: ${nullText(offer.org.name?.text)}`, style: 'p' },
    { text: `Org.nr: ${nullText(offer.org.orgNr?.text)}`, style: 'p' },
    { text: `Adresse: ${nullText(offer.org.address?.addressString)}`, style: 'p' },
    { text: 'Kontaktperson', style: 'h5' },
    { text: `Navn: ${nullText(offer.offerCreator.orgUser?.orgContactInfo?.name?.fullName)}`, style: 'p' },
    { text: `E-post: ${nullText(offer.offerCreator.orgUser?.orgContactInfo?.email?.email)}`, style: 'p' },
    { text: `Telefon: ${nullText(offer.offerCreator.orgUser?.orgContactInfo?.phone?.phoneNumberString)}`, style: 'p' },
    ...comments,
  ]
}

const getOfferTableHead = (hideUnitPrices: boolean): TableCell[] => {
  const cols: TableCell[] = [
    '',
    { alignment: 'right', text: 'Antall' },
  ]
  if (!hideUnitPrices) {
    cols.push({ alignment: 'right', text: 'Stykkpris' })
  }
  cols.push({ alignment: 'right', text: !hideUnitPrices ? 'Pris' : '' })
  return cols
}

function getOfferZonePriceFieldRow(offerRoomPriceField: OfferRoomPriceField, hideUnitPrices: boolean): TableCell[] {
  const subTotal = offerRoomPriceField.count * offerRoomPriceField.priceField.price
  const cols: TableCell[] = [
    { text: offerRoomPriceField.priceField.name, },
    { alignment: 'right', text: offerRoomPriceField.count, },
  ]
  if (!hideUnitPrices) {
    cols.push({ alignment: 'right', text: !hideUnitPrices ? currencyFormat(offerRoomPriceField.priceField.price) : '', })
  }
  cols.push({ alignment: 'right', text: !hideUnitPrices ? currencyFormat(subTotal) : '', })
  return cols
}

function getOfferTableTotalRows(finalPrice: number, hideUnitPrices: boolean, manualPrice?: Nullable<number>, discount?: Nullable<number>): TableCell[][] {
  const border = [false, true, false, false]
  const discountRow: TableCell[] | null = discount ? ([
    { colSpan: hideUnitPrices ? 2 : 3, text: 'Rabatt' + (isNumber(discount) ? `(-${Math.abs(calculateDiscountPercentage({ finalPrice, manualPrice, discount }))}%)` : ''), border },
    {},
  ]) : null
  if (discountRow) {
    if (!hideUnitPrices) {
      discountRow.push({})
    }
    discountRow.push({ alignment: 'right', text: isNumber(discount) ? `-${currencyFormat(Math.abs(discount))}` : '', border, })
  }

  const totalRow: TableCell[] = [
    { colSpan: hideUnitPrices ? 2 : 3, text: 'Totalt ekskl. mva.', bold: true, border: discount ? undefined : border },
    {},
  ]

  if (!hideUnitPrices) {
    totalRow.push({})
  }
  totalRow.push({ alignment: 'right', text: currencyFormat(finalPrice) ?? '', bold: true, border: discount ? undefined : border })

  return discountRow ? [discountRow, totalRow] : [totalRow]
}

function getDetailedOfferTable(offerDoc: DbDoc<Offer>): Content {
  const offer = offerDoc.data
  const headerRow = getOfferTableHead(offer.hideUnitPrices)

  const rows: TableCell[][] = Object
    .values(offer.offerRooms)
    .sort((a, b) => sortAlphabetically(a.room.name, b.room.name))
    .reduce((acc, offerRoom): TableCell[][] => {
      const name = offer.offerRoomNames[offerRoom.id] || offerRoom.room.name
      const comment = offer.offerRoomComments[offerRoom.id]

      const roomRow: TableCell[] = [
        { text: name, style: { fillColor: '#ddd', bold: true }, colSpan: offer.hideUnitPrices ? 3 : 4 },
      ]

      const roomCommentRow: TableCell[] = [
        { text: comment, style: { fillColor: '#efefef' }, colSpan: offer.hideUnitPrices ? 3 : 4 },
      ]

      const priceFieldRows: TableCell[][] = Object
        .values(offerRoom.priceFields)
        .map((offerRoomPriceField): TableCell[] => {
          const priceFieldRow: TableCell[] = getOfferZonePriceFieldRow(offerRoomPriceField, offer.hideUnitPrices)
          return priceFieldRow
        })

      return [
        ...acc,
        roomRow,
        ...(comment ? [roomCommentRow] : []),
        ...priceFieldRows,
      ]
    }, [] as TableCell[][])

  const body: TableCell[][] = [
    headerRow,
    ...rows,
    ...getOfferTableTotalRows(offer.price.finalPrice, offer.hideUnitPrices, offer.price.manualPrice, offer.price.discount),
  ]

  return {
    layout: {
      defaultBorder: false,
    },
    table: {
      headerRows: 1,
      widths: offer.hideUnitPrices ? [95, 25, 40] : [95, 25, 40, 40],
      body,
    }
  }
}

function getTotalTable(offer: DbDoc<Offer>): ContentTable {
  return {
    layout: 'noBorders',
    table: {
      headerRows: 1,
      widths: [100, 80],
      body: [
        ['Sum ekskl.mva.', { text: currencyFormat(offer.data.price.finalPrice), alignment: 'right' }],
        ['Mva-sats', { text: '25%', alignment: 'right' }],
        ['Sum mva', { text: currencyFormat(offer.data.price.finalPrice * 0.25), alignment: 'right' }],
        [{ text: 'Sum inkl.mva.', bold: true }, { bold: true, text: currencyFormat(offer.data.price.finalPrice * 1.25), alignment: 'right' }],
      ],
    }
  }
}

function getTotalPage(offer: DbDoc<Offer>): Content {
  return [
    offer.data.org.offerTerms ? new StateToPdfMake(offer.data.org.offerTerms
      //, {
      //  defaultStyles: {
      //    p: {
      //      margin: [0, 4, 0, 0],
      //    },
      //  },
      //}
    ).generate().content : '',
    {
      absolutePosition: { x: 65, y: 640 },
      stack: [
        getTotalTable(offer),
        { text: 'Signer nedenfor for å akseptere tilbudet', margin: [0, 20, 0, 40] },
        {
          canvas:
            [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 260, y2: 0,
                lineWidth: 1
              }
            ]
        },
      ]
    },
  ]
}


function getOrderAttachmentDescription(offer: DbDoc<Offer>, logoImageFileUrl: string, onlyLastPage: boolean): TDocumentDefinitions {
  const content: Content[] = onlyLastPage ? [] : [
    {
      stack: [
        { text: 'Tilbud', style: { alignment: 'center', bold: true, fontSize: 18 } },
      ],
      margin: [0, 0, 0, 30],
    },
    {
      columns: [
        {
          stack: [getOfferDetails(offer, logoImageFileUrl)],
          width: '50%'
        },
        {
          stack: [
            getDetailedOfferTable(offer),
          ],
        },
      ],
      pageBreak: 'after'
    },
  ]

  return {
    pageSize: 'A4',
    pageMargins: [65, 45, 65, 45],
    defaultStyle: {
      fontSize: 9,
    },
    styles: {
      p: {
        margin: [0, 4, 0, 0],
      },
      h3: {
        margin: [0, 12, 6, 2],
        bold: true,
        fontSize: 11,
      },
      h5: {
        margin: [0, 12, 4, 0],
        bold: true,
      },
    },
    footer(current, total) {
      return { text: `Side ${current} av ${total}`, style: 'p', alignment: 'center' }
    },
    content: {
      stack: [
        ...content,
        getTotalPage(offer),
      ],
    }
  }
}

//function useImageUrl(url: string) {
//  const [localUrl, setLocalUrl] = useState<null | string>(null)
//  useEffect(() => {
//    getBase64ImageFromURL(url).then(setLocalUrl)
//  }, [url, setLocalUrl])
//  return [localUrl]
//}


export async function getOfferPdf(offer: DbDoc<Offer>, onlyLastPage: boolean) {
  const logoImageFileUrl = await getBase64ImageFromURL(offer.data.org.logo?.original.url || LOGO_URL)
  const desc = getOrderAttachmentDescription(offer, logoImageFileUrl, onlyLastPage)

  const data = await new Promise<string>((resolve) => {
    pdfMake.createPdf(desc).getBase64(data => {
      resolve(data)
    })
  })

  return data
}

const [offerObservable$, pushOffer] = createSignal<[offer: DbDoc<Offer>, onlyLastPage: boolean]>();

export const [usePdfData, pdfData$] = bind(
  offerObservable$
    .pipe(
      debounceTime(500),
      concatMap(([offer, onlyLastPage]) => getOfferPdf(offer, onlyLastPage)),
    ), null)


const useStyles = makeStyles((theme) => ({
  pdfDocument: {
    width: 'min-content',
  }
}));

export function OfferPdfViewer({ offer, scale, onlyLastPage }: { offer: DbDoc<Offer>, scale: number, onlyLastPage?: boolean }) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const pdfData = usePdfData()

  useEffect(() => {
    if (offer) {
      pushOffer([offer, !!onlyLastPage])
    }
  }, [offer, onlyLastPage])

  function changePage(offset: number) {
    setPageNumber(prevPageNumber => prevPageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  /*
  //@ts-ignore
  const [logoImageFileUrl] = useImageUrl(TE_LOGO_URL || offer.org.logoUrl || LOGO_URL)
  const [scroll] = useState({ y: 0 })
  const [pdfUrl, setPdfUrl] = useState('')
  useEffect(() => {
    //const iframe: HTMLIFrameElement = document.getElementById('pdf-iframe') as HTMLIFrameElement
    if (logoImageFileUrl) {
      console.log('v')
      const o: Offer = {
        ...offer,
        org: {
          ...offer.org,
          offerTerms,
        },
      }
      const desc = getOrderAttachmentDescription(o, logoImageFileUrl)

      pdfMake.createPdf(desc).getBase64(pdfData => {
        setPdfUrl(pdfData)

        /*
            console.log({ pdfData })
            var pdfjsLib: any = window['pdfjs-dist/build/pdf' as any]
            // The workerSrc property shall be specified.
            pdfjsLib.GlobalWorkerOptions.workerSrc = '//mozilla.github.io/pdf.js/build/pdf.worker.js';
    
            // Using DocumentInitParameters object to load binary data.
            var loadingTask = pdfjsLib.getDocument({ data: atob(pdfData) })
            console.log(pdfjsLib, 'kuyeagfksjhgjk')
            loadingTask.promise.then(function (pdf: any) {
              console.log('PDF loaded');
    
              // Fetch the first page
              var pageNumber = 4;
              pdf.getPage(pageNumber).then(function (page: any) {
                console.log('Page loaded');
    
                var scale = 1.5;
                var viewport = page.getViewport({ scale: scale });
    
                // Prepare canvas using PDF page dimensions
                var canvas = document.getElementById('the-canvas') as HTMLCanvasElement;
                var context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;
    
                // Render PDF page into canvas context
                var renderContext = {
                  canvasContext: context,
                  viewport: viewport
                };
                var renderTask = page.render(renderContext);
                renderTask.promise.then(function () {
                  console.log('Page rendered');
                });
              });
            }, function (reason: any) {
              // PDF loading error
              console.error(reason);
            });
      })

      //if (!maga.stream) {
      //  const l = pdfMake.createPdf(desc)
      //  l.getBuffer(r => {
      //    console.log({ r })
      //  })
      //  maga.doc = l.getStream()
      //  maga.stream = maga.doc.pipe(blobStream())
      //} else {
      //  maga.doc.addContent(desc)
      //  //maga.doc.end()
      //}
      //(maga.stream as blobStream.IBlobStream)?.on('data', function () {
      //const iframe: HTMLIFrameElement = document.getElementById('pdf-iframe') as HTMLIFrameElement
      //iframe?.contentWindow?.location.reload()
      //  if (iframe) {
      //    iframe.src = maga.stream.toBlobURL('application/pdf');
      //  }
      //});



    }
  }, [logoImageFileUrl, offerTerms, offer])
  console.log({ pdfUrl })
  */

  const classes = useStyles()

  //console.log({ numPages })

  const pageButtons = (pdfData && numPages && (numPages > 1)) ? (
    <div style={{ justifyContent: 'center', display: 'grid', marginTop: 20, width: '100%' }}>
      <ButtonGroup variant="contained" color="secondary" size="small">
        <Button
          disabled={pageNumber <= 1}
          onClick={previousPage}
          startIcon={<ChevronLeft />}>
          Forige Side
        </Button>
        <Button onClick={() => setPageNumber(1)}>
          Side {pageNumber || (numPages ? 1 : "--")} av {numPages || "--"}
        </Button>
        <Button
          disabled={pageNumber >= numPages}
          onClick={nextPage}
          endIcon={<ChevronRight />}>
          Neste Side
        </Button>
      </ButtonGroup>
    </div>
  ) : null

  return (

    //<object data={`data:application/pdf;base64,${pdfUrl
    //  }`} type="application/pdf">
    //  <embed src={`data:application/pdf;base64,${pdfUrl
    //    }`} type="application/pdf" />
    //</object>
    //<>
    //pdfData &&
    <div>
      {pageButtons}
      <Paper style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: 20, marginBottom: 20, width: 'min-content', borderRadius: 18, overflow: 'hidden' }}>
        <Document
          className={classes.pdfDocument}
          loading={<div style={{ width: 595.28 * scale, height: 841.89 * scale, background: 'white' }}></div>}
          noData={<div style={{ width: 595.28 * scale, height: 841.89 * scale, background: 'white' }}></div>}
          file={pdfData && `data:application/pdf;base64,${pdfData}`}
          onLoadSuccess={({ numPages: numDocPages }) => { setNumPages(numDocPages) }}
        >
          <Page scale={scale} pageNumber={pageNumber} />
        </Document>
      </Paper>
      {pageButtons}
      <Box mt={5} />
    </div>
    //</>

    //<canvas id="the-canvas" width="100%" style={{ height: '100vh', background: 'white' }}></canvas>

    //<iframe id="pdf-iframe" src={`data:application/pdf;base64,${pdfUrl
    //  }`} title="PDF" style={{ height: '100vh', width: '47vw', float: 'right' }} />
  )
}

export async function generateOfferPdf(offer: Offer) {
  // create a document and pipe to a blob
  const doc: any = {}//pdfMake.createPdf(await getOrderAttachmentHtml(offer)).getStream()

  var stream = doc.pipe(blobStream());

  //// draw some text
  //doc.fontSize(25).text('Here is some vector graphics...', 100, 80);
  //
  //// some vector graphics
  //doc
  //  .save()
  //  .moveTo(100, 150)
  //  .lineTo(100, 250)
  //  .lineTo(200, 250)
  //  .fill('#FF3300');
  //
  //doc.circle(280, 200, 50).fill('#6600FF');
  //
  //// an SVG path
  //doc
  //  .scale(0.6)
  //  .translate(470, 130)
  //  .path('M 250,75 L 323,301 131,161 369,161 177,301 z')
  //  .fill('red', 'even-odd')
  //  .restore();
  //
  //// and some justified text wrapped into columns
  //doc
  //  .text('And here is some wrapped text...', 100, 300)
  //  .moveDown()
  //  .text(lorem, {
  //    width: 412,
  //    align: 'justify',
  //    indent: 30,
  //    columns: 2,
  //    height: 300,
  //    ellipsis: true
  //  });
  //
  doc.end()

  stream.on('finish', function () {
    const iframe: HTMLIFrameElement = document.getElementById('pdf-iframe') as HTMLIFrameElement
    if (iframe) {
      iframe.src = stream.toBlobURL('application/pdf');
    }
  });
}
